'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  base64urlToBuffer,
  bufferToBase64url,
  decodeCreationOptions,
  decodeRequestOptions,
  encodeAssertionResponse,
  encodeAttestationResponse,
  webauthnFetchJSON,
  WebauthnFetchError,
} from '@/lib/passkey/client';

// ---------------------------------------------------------------------------
// Shared response types
// ---------------------------------------------------------------------------

export type PasskeyCredentialSummary = {
  id: string;
  name: string;
  aaguid: string | null;
  transports: string[];
  backupEligible: boolean;
  backupState: boolean;
  /** True if the credential has been marked trusted by an admin. */
  trusted: boolean;
  trustedAt: string | null;
  hardwareBacked: boolean;
  createdAt: string;
  /**
   * Legacy alias of `createdAt` — kept for forward-compat in case a client build
   * is ahead of a server build (or vice versa). Always prefer `createdAt` when
   * both are present; consumers should read `createdAt ?? addedOn`.
   */
  addedOn?: string;
  lastUsedAt: string | null;
  lastUsedIP: string | null;
  lastUsedUserAgent: string | null;
  attestationFormat: string | null;
  disabledAt: string | null;
  disabledReason: string | null;
};

export type PasskeyRegisterResult = {
  credential: PasskeyCredentialSummary;
  /** Populated on first enrollment only; shown once to the user. */
  recoveryCodes?: string[];
};

export type PasskeySession = {
  user: {
    id: string;
    email: string;
    name: string | null;
    role: string;
  };
  authMethod: string;
  /** When conditional/autofill flow ran, the server may signal nextStep='two-factor'. */
  nextStep?: 'authenticated' | 'two-factor';
  message?: string;
};

export type UsePasskeyReturn = {
  /** True when the browser exposes WebAuthn APIs. */
  isSupported: boolean;
  /** True when conditional mediation (autofill-first passkey UI) is available. */
  isConditionalAvailable: boolean;
  registerPasskey: (name?: string) => Promise<PasskeyRegisterResult>;
  signInWithPasskey: (email?: string) => Promise<PasskeySession>;
  /**
   * Starts the conditional-UI flow (autofill). Returns a cancel function.
   * On success the promise resolves with the session via `onSuccess`.
   */
  signInWithPasskeyConditional: (handlers: {
    onSuccess: (session: PasskeySession) => void;
    onError?: (err: Error) => void;
  }) => () => void;
  listPasskeys: () => Promise<PasskeyCredentialSummary[]>;
  /** Deletes a passkey. Performs a user-verification assertion first; sends it as X-Passkey-UV-Assertion. */
  deletePasskey: (id: string) => Promise<void>;
  /** Renames a passkey. Performs a user-verification assertion first; sends it as X-Passkey-UV-Assertion. */
  renamePasskey: (id: string, name: string) => Promise<void>;
  /** Admin-only: mark a passkey as trusted. Requires a fresh UV assertion, sent as X-Passkey-UV-Assertion. */
  markPasskeyTrusted: (id: string) => Promise<void>;
  requestRecovery: (email: string) => Promise<{ ok: true; message?: string }>;
  /** Consume the magic-link recovery URL token. */
  consumeRecovery: (token: string) => Promise<PasskeySession>;
  /** Consume a one-time recovery code (alternative to magic-link). */
  consumeRecoveryCode: (email: string, code: string) => Promise<PasskeySession>;
  /**
   * Low-level helper exposed for UI pages (policy patch/approve, compliance
   * CSV export) that must attach their own `X-Passkey-UV-Assertion` header.
   * Returns the base64url-encoded header value. Throws on cancel.
   */
  createUvAssertion: (purpose: string) => Promise<string>;
};

// ---------------------------------------------------------------------------
// UV re-auth helper
// ---------------------------------------------------------------------------

/**
 * Produces a fresh UV-required assertion (a "step-up" WebAuthn authentication)
 * and returns it base64url-encoded for use as the `X-Passkey-UV-Assertion`
 * header value.
 *
 * The assertion contains a new server-minted challenge bound to the requested
 * purpose (e.g. `delete_passkey`, `regenerate_recovery_codes`). The server
 * verifies it before allowing the destructive action.
 */
async function getUvAssertionHeader(purpose: string): Promise<string> {
  const opts = await webauthnFetchJSON<{ publicKey: unknown; challengeToken?: string }>(
    '/api/auth/passkey/uv-challenge',
    { purpose },
  );
  const decoded = decodeRequestOptions(
    (opts as { publicKey: Parameters<typeof decodeRequestOptions>[0] }).publicKey ??
      (opts as unknown as Parameters<typeof decodeRequestOptions>[0]),
  );
  // Force UV=required on the client as an additional defense; the server sets it too.
  decoded.userVerification = 'required';

  const cred = (await navigator.credentials.get({ publicKey: decoded })) as PublicKeyCredential | null;
  if (!cred) {
    throw new Error('Passkey verification was cancelled');
  }
  const payload = {
    purpose,
    challengeToken: (opts as { challengeToken?: string }).challengeToken,
    credential: encodeAssertionResponse(cred),
  };
  const json = JSON.stringify(payload);
  // Base64url the JSON payload so it's a safe single-line header.
  return bufferToBase64url(new TextEncoder().encode(json));
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function usePasskey(): UsePasskeyReturn {
  const [isConditionalAvailable, setIsConditionalAvailable] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const isSupported =
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    'credentials' in navigator &&
    typeof window.PublicKeyCredential !== 'undefined';

  useEffect(() => {
    if (!isSupported) return;
    const pkc = window.PublicKeyCredential as typeof PublicKeyCredential & {
      isConditionalMediationAvailable?: () => Promise<boolean>;
    };
    if (typeof pkc.isConditionalMediationAvailable === 'function') {
      pkc
        .isConditionalMediationAvailable()
        .then((available) => setIsConditionalAvailable(Boolean(available)))
        .catch(() => setIsConditionalAvailable(false));
    }
  }, [isSupported]);

  // -------------------------------------------------------------------------
  // Registration
  // -------------------------------------------------------------------------

  const registerPasskey = useCallback(
    async (name?: string): Promise<PasskeyRegisterResult> => {
      if (!isSupported) {
        throw new Error('WebAuthn is not supported in this browser');
      }

      const begin = await webauthnFetchJSON<{ publicKey: unknown; challengeToken?: string }>(
        '/api/auth/passkey/register/begin',
        { name },
      );
      const publicKey = decodeCreationOptions(
        (begin as { publicKey: Parameters<typeof decodeCreationOptions>[0] }).publicKey ??
          (begin as unknown as Parameters<typeof decodeCreationOptions>[0]),
      );

      const cred = (await navigator.credentials.create({ publicKey })) as PublicKeyCredential | null;
      if (!cred) {
        throw new Error('Passkey registration was cancelled');
      }

      const finish = await webauthnFetchJSON<PasskeyRegisterResult>('/api/auth/passkey/register/finish', {
        name,
        challengeToken: (begin as { challengeToken?: string }).challengeToken,
        credential: encodeAttestationResponse(cred),
      });
      return finish;
    },
    [isSupported],
  );

  // -------------------------------------------------------------------------
  // Sign-in (explicit button)
  // -------------------------------------------------------------------------

  const signInWithPasskey = useCallback(
    async (email?: string): Promise<PasskeySession> => {
      if (!isSupported) {
        throw new Error('WebAuthn is not supported in this browser');
      }

      // Single source of truth for active WebAuthn ops: abort whatever is
      // in flight (most commonly the background conditional-UI session
      // started on mount) so Safari/Chrome release the WebAuthn lock
      // before we open the explicit modal picker. Without this, the
      // conditional `credentials.get()` holds the lock and the new
      // explicit call hangs silently until the 5-min server timeout.
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      // Defensive user-visible timeout: if the native picker never
      // appears (no passkey on device, UA bug, …) surface an error
      // instead of a forever-spinning button. 20s is long enough for
      // real biometric auth but short enough that users don't wait.
      const PASSKEY_EXPLICIT_TIMEOUT_MS = 20_000;
      const timeoutId = setTimeout(() => {
        controller.abort(new DOMException('Passkey picker timed out', 'PasskeyTimeoutError'));
      }, PASSKEY_EXPLICIT_TIMEOUT_MS);

      try {
        const begin = await webauthnFetchJSON<{ publicKey: unknown; challengeToken?: string }>(
          '/api/auth/passkey/login/begin',
          { email },
          { authenticated: false, signal: controller.signal },
        );
        const publicKey = decodeRequestOptions(
          (begin as { publicKey: Parameters<typeof decodeRequestOptions>[0] }).publicKey ??
            (begin as unknown as Parameters<typeof decodeRequestOptions>[0]),
        );

        const cred = (await navigator.credentials.get({
          publicKey,
          signal: controller.signal,
        } as CredentialRequestOptions)) as PublicKeyCredential | null;
        if (!cred) {
          throw new Error('Passkey sign-in was cancelled');
        }

        const session = await webauthnFetchJSON<PasskeySession>(
          '/api/auth/passkey/login/finish',
          {
            challengeToken: (begin as { challengeToken?: string }).challengeToken,
            credential: encodeAssertionResponse(cred),
          },
          { authenticated: false, signal: controller.signal },
        );
        return session;
      } finally {
        clearTimeout(timeoutId);
        if (abortRef.current === controller) {
          abortRef.current = null;
        }
      }
    },
    [isSupported],
  );

  // -------------------------------------------------------------------------
  // Sign-in (conditional UI / autofill)
  // -------------------------------------------------------------------------

  const signInWithPasskeyConditional = useCallback(
    (handlers: { onSuccess: (session: PasskeySession) => void; onError?: (err: Error) => void }) => {
      if (!isSupported || !isConditionalAvailable) {
        return () => {};
      }

      const abort = new AbortController();
      abortRef.current?.abort();
      abortRef.current = abort;

      (async () => {
        try {
          const begin = await webauthnFetchJSON<{ publicKey: unknown; challengeToken?: string }>(
            '/api/auth/passkey/login/begin',
            { conditional: true },
            { authenticated: false, signal: abort.signal },
          );
          const publicKey = decodeRequestOptions(
            (begin as { publicKey: Parameters<typeof decodeRequestOptions>[0] }).publicKey ??
              (begin as unknown as Parameters<typeof decodeRequestOptions>[0]),
          );
          const cred = (await navigator.credentials.get({
            publicKey,
            mediation: 'conditional',
            signal: abort.signal,
          } as CredentialRequestOptions)) as PublicKeyCredential | null;

          if (!cred || abort.signal.aborted) return;

          const session = await webauthnFetchJSON<PasskeySession>(
            '/api/auth/passkey/login/finish',
            {
              challengeToken: (begin as { challengeToken?: string }).challengeToken,
              credential: encodeAssertionResponse(cred),
            },
            { authenticated: false, signal: abort.signal },
          );
          if (!abort.signal.aborted) {
            handlers.onSuccess(session);
          }
        } catch (err) {
          if (abort.signal.aborted) return;
          // AbortError: user navigated / unmounted. Swallow silently.
          if (err instanceof DOMException && err.name === 'AbortError') return;
          const error = err instanceof Error ? err : new Error(String(err));
          handlers.onError?.(error);
        }
      })();

      return () => {
        abort.abort();
        if (abortRef.current === abort) abortRef.current = null;
      };
    },
    [isSupported, isConditionalAvailable],
  );

  // Abort any pending conditional call on unmount.
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, []);

  // -------------------------------------------------------------------------
  // Management
  // -------------------------------------------------------------------------

  const listPasskeys = useCallback(async (): Promise<PasskeyCredentialSummary[]> => {
    const resp = await webauthnFetchJSON<{ passkeys?: PasskeyCredentialSummary[] } | PasskeyCredentialSummary[]>(
      '/api/user/passkeys',
    );
    if (Array.isArray(resp)) return resp;
    return resp?.passkeys ?? [];
  }, []);

  // Passkey self-management (delete / rename / mark-trusted) no longer
  // gates on a UV-reauth assertion. Server-side gate was removed
  // 2026-05-16 — see auth_passkey.go around line 1060 / 1130 for the
  // rationale (Lovable-parity, session cookie + ownership check
  // is sufficient).
  const deletePasskey = useCallback(async (id: string): Promise<void> => {
    await webauthnFetchJSON<void>(`/api/user/passkeys/${encodeURIComponent(id)}`, undefined, {
      method: 'DELETE',
    });
  }, []);

  const renamePasskey = useCallback(async (id: string, name: string): Promise<void> => {
    await webauthnFetchJSON<void>(`/api/user/passkeys/${encodeURIComponent(id)}`, { name }, { method: 'PATCH' });
  }, []);

  const markPasskeyTrusted = useCallback(async (id: string): Promise<void> => {
    await webauthnFetchJSON<void>(
      `/api/user/passkeys/${encodeURIComponent(id)}`,
      { trusted: true },
      { method: 'PATCH' },
    );
  }, []);

  // -------------------------------------------------------------------------
  // Recovery
  // -------------------------------------------------------------------------

  const requestRecovery = useCallback(async (email: string) => {
    return webauthnFetchJSON<{ ok: true; message?: string }>(
      '/api/auth/passkey/recovery/request',
      { email },
      { authenticated: false },
    );
  }, []);

  const consumeRecovery = useCallback(async (token: string): Promise<PasskeySession> => {
    return webauthnFetchJSON<PasskeySession>('/api/auth/passkey/recovery/consume', { token }, { authenticated: false });
  }, []);

  const consumeRecoveryCode = useCallback(async (email: string, code: string): Promise<PasskeySession> => {
    return webauthnFetchJSON<PasskeySession>(
      '/api/auth/passkey/recovery/consume-code',
      { email, code },
      { authenticated: false },
    );
  }, []);

  const createUvAssertion = useCallback(async (purpose: string) => {
    return getUvAssertionHeader(purpose);
  }, []);

  return {
    isSupported,
    isConditionalAvailable,
    registerPasskey,
    signInWithPasskey,
    signInWithPasskeyConditional,
    listPasskeys,
    deletePasskey,
    renamePasskey,
    markPasskeyTrusted,
    requestRecovery,
    consumeRecovery,
    consumeRecoveryCode,
    createUvAssertion,
  };
}

// Re-export helper + error so consumers (settings UI, tests) can import from one place.
export { base64urlToBuffer, bufferToBase64url, WebauthnFetchError };
